import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { listMyYearBundles, type AcademicYearBundle } from '../../services/academicYearBundleService';
import { getSession } from '../../services/authService';
import { syncMySubscriptionPassState } from '../../services/platformUserSubscriptionPassService';
import { currentAcademicYear, isAcademicYearLocked, passYearOfEnd } from '../../utils/classAcademicYear';
import { fetchMyPassYears, switchAcademicYear, useSelectedAcademicYear } from '../../utils/selectedAcademicYear';
import {
  eventPassKindLabel,
  loadSubscriptionPassState,
  resolveUserPlanTier,
} from '../../utils/subscriptionPassStorage';
import { PLATFORM_TEST_ACCOUNT_EMAIL } from '../../types/billingSettings';
import './AcademicYearSelector.css';

type YearPass = { kindLabel: string; start: Date; end: Date };

const fmt = (d: Date) =>
  `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;

/** 학년도 Y 기본 이용기간(이용권이 없을 때 표시): Y.03.01 ~ Y+1.02 말일 */
const defaultPeriod = (y: number) => `${y}.03.01 ~ ${fmt(new Date(y + 1, 2, 0))}`;

type CardProps = {
  year: number;
  pass?: YearPass;
  status: 'current' | 'next' | 'locked' | 'needs-pass';
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  hint?: string;
};

const MiniPassCard: React.FC<CardProps> = ({ year, pass, status, selected, disabled, onClick, hint }) => {
  const badge = pass ? pass.kindLabel : status === 'needs-pass' ? '이용권 없음' : 'FREE';
  const suffix = status === 'locked' ? ' (기록 중지)' : status === 'next' ? ' (다음)' : status === 'needs-pass' ? ' (이용권 필요)' : ' (현재)';
  return (
    <button
      type="button"
      className={[
        'cp-mini-pass-card',
        pass ? 'is-plus' : 'is-free',
        status === 'locked' ? 'is-locked' : '',
        disabled ? 'is-disabled' : '',
        selected ? 'is-selected' : '',
      ].join(' ')}
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
    >
      <span className="cp-mini-pass-card__top">
        <span className="cp-mini-pass-card__badge">{badge}</span>
        <span className="cp-mini-pass-card__tier">{pass ? 'PLUS 이용권' : 'FREE 이용권'}</span>
      </span>
      <span className="cp-mini-pass-card__title">
        {year}학년도 이용권<span className="cp-mini-pass-card__suffix">{suffix}</span>
      </span>
      <span className="cp-mini-pass-card__period">
        이용기간 {pass ? `${fmt(pass.start)} ~ ${fmt(pass.end)}` : defaultPeriod(year)}
      </span>
      {hint ? <span className="cp-mini-pass-card__hint">{hint}</span> : null}
    </button>
  );
};

/** 계정(햄버거) 메뉴의 전역 학년도 선택기: 미니 이용권 카드 + 학년도 선택 모달 */
const AcademicYearSelector: React.FC = () => {
  const selected = useSelectedAcademicYear();
  const [bundles, setBundles] = useState<AcademicYearBundle[]>([]);
  const [passYears, setPassYears] = useState<number[]>([]);
  const [passes, setPasses] = useState<Record<number, YearPass>>({});
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void listMyYearBundles().then((rows) => !cancelled && setBundles(rows));
    void fetchMyPassYears().then((ys) => !cancelled && setPassYears(ys));
    void getSession()
      .then(async (session) => {
        const uid = session?.user?.id;
        if (!uid) return;
        const { state } = await syncMySubscriptionPassState(uid);
        const map: Record<number, YearPass> = {};
        const add = (kindLabel: string, start: Date, end: Date, refunded?: Date) => {
          if (refunded) return;
          const s = new Date(start);
          const e = new Date(end);
          if (Number.isNaN(e.getTime())) return;
          const y = passYearOfEnd(e);
          if (!map[y] || map[y].end < e) map[y] = { kindLabel, start: s, end: e };
        };
        state.individualHistory.forEach((r) => add('개별 이용권', r.periodStart, r.periodEnd, r.refundedAt));
        state.eventHistory.forEach((r) => add(eventPassKindLabel(r), r.periodStart, r.periodEnd, r.refundedAt));
        state.groupHistory.forEach((r) =>
          add('그룹 이용권', r.activationPeriodStart, r.activationPeriodEnd, r.refundedAt)
        );
        // 상단 플랜 표시와 같은 기준(resolveUserPlanTier)으로 PLUS인데 이용권 이력이 없으면 관리자 지정/테스트로 표시
        const cy = currentAcademicYear();
        if (!map[cy] && resolveUserPlanTier(loadSubscriptionPassState()) === 'PLUS') {
          const isTest =
            (session?.user?.email ?? '').trim().toLowerCase() === PLATFORM_TEST_ACCOUNT_EMAIL.trim().toLowerCase();
          const ev = loadSubscriptionPassState().eventPassPeriod;
          const s = ev ? new Date(ev.periodStart) : new Date(cy, 2, 1);
          const e = ev ? new Date(ev.periodEnd) : new Date(cy + 1, 2, 0);
          map[cy] = { kindLabel: isTest ? '테스트' : '관리자 지정', start: s, end: e };
        }
        if (!cancelled) setPasses(map);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);

  const cur = currentAcademicYear();
  const nextHasPass = passYears.includes(cur + 1) || !!passes[cur + 1];

  const years = useMemo(() => {
    const set = new Set<number>([cur, cur + 1, selected]);
    bundles.forEach((b) => set.add(b.academic_year));
    return Array.from(set)
      .filter((y) => y <= cur + 1)
      .sort((a, b) => a - b);
  }, [bundles, selected, cur]);

  const statusOf = (y: number): CardProps['status'] =>
    y > cur ? (nextHasPass ? 'next' : 'needs-pass') : isAcademicYearLocked(y) ? 'locked' : 'current';

  const choose = async (y: number) => {
    if (y === selected) {
      setOpen(false);
      return;
    }
    setBusy(true);
    setError('');
    const ok = await switchAcademicYear(y);
    if (!ok) {
      setBusy(false);
      setError(
        y > cur && !nextHasPass
          ? `${y}학년도 이용권이 있어야 선택할 수 있습니다.`
          : '학년도를 바꾸지 못했습니다. 잠시 후 다시 시도해 주세요.'
      );
    }
  };

  const modal = open
    ? createPortal(
        <div
          className="cp-year-modal-backdrop"
          onMouseDown={(e) => {
            e.stopPropagation();
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="cp-year-modal" role="dialog" aria-modal="true" aria-labelledby="cp-year-modal-title">
            <div className="cp-year-modal__head">
              <h2 id="cp-year-modal-title">학년도 선택</h2>
              <button type="button" className="cp-year-modal__close" onClick={() => setOpen(false)} aria-label="닫기">
                ×
              </button>
            </div>
            <p className="cp-year-modal__desc">학년도를 고르면 그 학년도의 클래스와 기록으로 바뀝니다.</p>
            <div className="cp-year-modal__list">
              {years.map((y) => {
                const st = statusOf(y);
                return (
                  <MiniPassCard
                    key={y}
                    year={y}
                    pass={passes[y]}
                    status={st}
                    selected={y === selected}
                    disabled={busy || st === 'needs-pass'}
                    onClick={() => void choose(y)}
                    hint={
                      st === 'needs-pass'
                        ? `${y}학년도 이용권을 구매하면 선택할 수 있습니다.`
                        : st === 'locked'
                          ? '읽기 전용 · 설정 › 클래스에서 내려받기'
                          : undefined
                    }
                  />
                );
              })}
            </div>
            {error ? (
              <p className="cp-year-selector-error" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="cp-year-selector" role="group" aria-label="학년도 선택">
      <MiniPassCard
        year={selected}
        pass={passes[selected]}
        status={statusOf(selected)}
        onClick={() => setOpen(true)}
        hint="눌러서 학년도 바꾸기"
      />
      {modal}
    </div>
  );
};

export default AcademicYearSelector;
